import fs from 'node:fs'
import path from 'node:path'

import { parseVercelJson, runVercel } from './runner.js'
import type { VercelProject, VercelRunner } from './types.js'

/**
 * Sanitize a project name to Vercel's rules: lowercase, only
 * `[a-z0-9._-]`, no leading/trailing separators, max 100 chars.
 */
export function sanitizeVercelProjectName(name: string): string {
  const cleaned = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[-_.]+/, '')
    .replace(/[-_.]+$/, '')
    .slice(0, 100)
  return cleaned || 'my-app'
}

/** Bounded name search: base plus -v2 … -v10 before giving up on fresh names. */
const MAX_NAME_ATTEMPTS = 10
const INSPECT_TIMEOUT_MS = 30_000

/**
 * Append a version suffix (`-v2`, `-v3`, …) while keeping the full name within
 * Vercel's 100-character limit. Attempt 1 is the base name itself.
 */
export function versionedVercelProjectName(base: string, attempt: number): string {
  if (attempt <= 1) return base
  const suffix = `-v${attempt}`
  return `${base.slice(0, 100 - suffix.length)}${suffix}`
}

/**
 * Create a fresh Vercel project and link the local directory to it (writes
 * `.vercel/project.json`). Reusing an existing project of the same name is
 * deliberately avoided: a reused project usually carries a `DATABASE_URL` env
 * var from a previous run, which makes the Neon integration's connect step fail
 * with a 400. Instead, taken names get a version suffix (`-v2`, `-v3`, …) until
 * a free one is found. Existence is probed with the read-only `project
 * inspect` (exit 0 = taken, scope-consistent with `projects add`); the probe is
 * load-bearing because modern CLIs (v54+) make `projects add` an idempotent
 * success on existing names. An "already exists" error from `projects add`
 * still catches conflicts on older CLIs. Any other `projects add` failure falls back
 * to linking that name anyway — the pre-suffix behavior — so provisioning is
 * never blocked here. The project id is read back from the linked
 * `.vercel/project.json`, the canonical source of truth.
 *
 * Scope handling: the first pass passes `options.scope` (the `whoami` username)
 * because Vercel CLI 56.2.1+ in non-TTY mode fails with `missing_scope` on
 * multi-scope accounts unless a scope is explicit. But on team-first accounts
 * (every account created since mid-2024), projects can only live in a team —
 * `whoami` still prints the bare username, which is not a project-owning scope
 * there, so every `--scope <username>` command fails. When the scoped pass ends
 * unlinked, a second unscoped pass runs; if its `link` hits the CLI's structured
 * `missing_scope` payload, the retry picks a real project-owning scope from the
 * payload's `choices` (the username's team when one matches, else the first
 * listed, which the CLI orders default-first).
 */
export async function createVercelProject(
  runner: VercelRunner,
  cwd: string,
  name: string,
  options: { env?: NodeJS.ProcessEnv; scope?: string } = {}
): Promise<VercelProject> {
  const base = sanitizeVercelProjectName(name)

  const scoped = await createAndLinkProject(runner, cwd, base, options)
  if (scoped.linked || !options.scope) return scoped

  return createAndLinkProject(runner, cwd, base, {
    env: options.env,
    preferredScope: options.scope
  })
}

async function createAndLinkProject(
  runner: VercelRunner,
  cwd: string,
  base: string,
  options: { env?: NodeJS.ProcessEnv; scope?: string; preferredScope?: string }
): Promise<VercelProject> {
  let projectName = base

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const candidate = versionedVercelProjectName(base, attempt)
    projectName = candidate

    const inspect = await runVercel(
      runner,
      withScope(['project', 'inspect', candidate], options.scope),
      {
        cwd,
        mode: 'capture',
        timeoutMs: INSPECT_TIMEOUT_MS,
        env: options.env
      }
    )
    if (inspect.success) continue // name taken → try the next version suffix

    const add = await runVercel(runner, withScope(['projects', 'add', candidate], options.scope), {
      cwd,
      mode: 'capture',
      env: options.env
    })
    if (add.success) break
    if (/already exists/i.test(`${add.stdout}\n${add.stderr}`)) continue
    break // unknown failure — link this name anyway rather than blocking setup
  }

  // Drop stale link state from a previous run: `vercel link` can resolve the
  // project name through the old file's org scope, which may differ from the
  // authenticated scope `inspect`/`add` just used and re-attach to the old project.
  try {
    fs.rmSync(path.join(cwd, '.vercel', 'project.json'), { force: true })
  } catch {
    // best effort — a stale file must never block provisioning
  }

  let link = await runVercel(
    runner,
    withScope(['link', '--project', projectName, '--yes'], options.scope),
    {
      cwd,
      mode: 'capture',
      env: options.env
    }
  )

  // Non-TTY `link` refuses to apply a default scope on multi-scope accounts and
  // instead prints a structured `missing_scope` payload listing the valid
  // project-owning scopes — retry once with a scope resolved from that list.
  if (!link.success) {
    const fallbackScope = resolveMissingScopeChoice(
      `${link.stdout}\n${link.stderr}`,
      options.scope ?? options.preferredScope
    )
    if (fallbackScope && fallbackScope !== options.scope) {
      link = await runVercel(
        runner,
        withScope(['link', '--project', projectName, '--yes'], fallbackScope),
        {
          cwd,
          mode: 'capture',
          env: options.env
        }
      )
    }
  }

  return {
    name: projectName,
    linked: link.success,
    projectId: readLinkedProjectId(cwd)
  }
}

function withScope(args: string[], scope?: string): string[] {
  return scope ? [...args, '--scope', scope] : args
}

/**
 * Pick a scope out of the Vercel CLI's non-TTY `missing_scope` payload
 * (`{"status":"action_required","reason":"missing_scope","choices":[{name}]}`).
 * A single choice wins outright; otherwise prefer the choice matching the
 * authenticated username, then a team slug derived from it (team-first
 * accounts name the personal team `<username>s-projects`), then the first
 * choice — the CLI lists the default scope first.
 */
export function resolveMissingScopeChoice(output: string, preferred?: string): string | undefined {
  if (!/missing_scope/.test(output)) return undefined

  const payload = parseVercelJson<{ reason?: string; choices?: Array<{ name?: string }> }>(output)
  if (payload?.reason !== 'missing_scope') return undefined

  const names = (payload.choices ?? [])
    .map((choice) => choice.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
  if (names.length === 0) return undefined
  if (names.length === 1) return names[0]

  if (preferred) {
    const match = names.find((name) => name === preferred || name.startsWith(preferred))
    if (match) return match
  }
  return names[0]
}

/** Project id from `.vercel/project.json`, the canonical link state (undefined when not linked). */
export function readLinkedProjectId(cwd: string): string | undefined {
  return readLinkedProjectJson(cwd)?.projectId
}

function readLinkedProjectJson(cwd: string): { projectId?: string } | undefined {
  try {
    const projectJsonPath = path.join(cwd, '.vercel', 'project.json')
    if (!fs.existsSync(projectJsonPath)) return undefined
    return JSON.parse(fs.readFileSync(projectJsonPath, 'utf-8')) as { projectId?: string }
  } catch {
    return undefined
  }
}
